import React, { Suspense, lazy, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ChatMarkdown from './components/ChatMarkdown';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './App.css';
// Side-copy redesign. Legacy stays untouched; v2 lives entirely under
// `./redesign/`. App.tsx is the ONLY file that knows about the flag.
import { consumeUIQueryParam, applyDesignAttribute, getUI } from './redesign/uiFlag';
import { consumeApiKeyQueryParam } from './apiKeySession';
import { capturePlaygroundHandoffFromUrl } from './redesign/lib/playgroundHandoff';
import { useUrl } from './redesign/nav/useUrl';
import { getV2Router } from './redesign/router';
import './redesign/theme/soft.css';
import { useWebSocket, ServerMessage, BotFeatures } from './hooks/useWebSocket';
import { useAudioPlayer } from './hooks/useAudioPlayer';
import { useScreenShareTab, ScreenShareConfig } from './hooks/useScreenShareTab';
import getVoiceRecorderSingleton from './components/Recorder';
import ChatInput from './components/ChatInput';
import { AuthnMeProvider, useAuthnMe, isAuthnMeConfigured } from './AuthnMeProvider';
import PreferencesGate from './PreferencesGate';
import { ThemeProvider } from './hooks/useTheme';
import useSetFavicon from './hooks/useSetFavicon';
import { getUrlParams } from './utils/urlParams';
import { legacyCallLogV2Url } from './utils/legacyCallLog';
import LoginButton from './components/LoginButton';
import HomePageWrapper from './components/HomePageWrapper';
import GlobeDialButton from './components/GlobeDialButton';
import ThemeToggle from './components/ThemeToggle';
import LoadingState from './components/LoadingState';
import type { ChatMessage } from './types/chat';
import { detectTextDirection } from './utils/textUtils';
import { cutoffSplit } from './utils/cutoff';
import { formatHandoffNotice } from './utils/handoff';
import type { FileType } from './utils/fileUtils';

const Widget = lazy(() => import('./Widget'));
const HeadlessWidget = lazy(() => import('./HeadlessWidget'));
const HelpPage = lazy(() => import('./HelpPage'));
const DemoPage = lazy(() => import('./DemoPage'));
const ApiDocsPage = lazy(() => import('./ApiDocsPage'));
const AdminPage = lazy(() => import('./AdminPage'));
const CustomerPage = lazy(() => import('./CustomerPage'));
const ConfigPage = lazy(() => import('./ConfigPage'));
const CallLogsPage = lazy(() => import('./CallLogsPage'));
const OperatePage = lazy(() => import('./OperatePage'));
const PrivacyPage = lazy(() => import('./PrivacyPage'));
const TermsPage = lazy(() => import('./TermsPage'));
const TaskBoardPage = lazy(() => import('./TaskBoardPage'));
const WorkflowListPage = lazy(() => import('./WorkflowListPage'));
const MoveBotWithLinkPage = lazy(() => import('./MoveBotWithLinkPage'));
const AcceptTenantPage = lazy(() => import('./AcceptTenantPage'));
const ResellerPage = lazy(() => import('./ResellerPage'));
const PackagesPage = lazy(() => import('./PackagesPage'));
const BillingUsagePage = lazy(() => import('./BillingUsagePage'));
const UsagePage = lazy(() => import('./UsagePage'));
const AdminBillingPage = lazy(() => import('./AdminBillingPage'));

// v2 redesigned pages — separate route trees from their legacy counterparts.
// When the user's `voxcore.ui` flag is 'v2', App.tsx dispatches to these
// instead of the legacy page.
const CustomerV2 = lazy(() => import('./redesign/pages/Customer'));
const BotDialV2 = lazy(() => import('./redesign/pages/BotDial'));   // /b/{id}
const SidekickHelperV2 = lazy(() => import('./redesign/components/SidekickHelper')); // embed helper/sidekick mode
const BotHeroV2 = lazy(() => import('./redesign/pages/BotHero'));   // /b/{id}/manage (legacy alias: /b/{id}/hero)
const ConfigureV2 = lazy(() => import('./redesign/pages/Configure'));// /b/{id}/config?tab=…
const TermsV2 = lazy(() => import('./redesign/pages/Terms'));        // /terms
const PrivacyV2 = lazy(() => import('./redesign/pages/Privacy'));    // /privacy
const CallLogsV2 = lazy(() => import('./redesign/pages/CallLogs')); // /b/{id}/call-logs
const OperateV2 = lazy(() => import('./redesign/pages/Operate'));   // /b/{id}/operate
const BillingUsageV2 = lazy(() => import('./redesign/pages/BillingUsage')); // /customer/{id}/billing
const PackagesV2 = lazy(() => import('./redesign/pages/Packages'));         // /customer/{id}/packages
const HelpV2 = lazy(() => import('./redesign/pages/Help'));         // /help (and /b/{id}/help)
const ApiDocsV2 = lazy(() => import('./redesign/pages/ApiDocs'));   // /api-docs (and /b/{id}/api-docs)
const AdminV2 = lazy(() => import('./redesign/pages/Admin'));       // /admin
const ResellerV2 = lazy(() => import('./redesign/pages/Reseller'));         // /reseller
const AdminBillingV2 = lazy(() => import('./redesign/pages/AdminBilling')); // /admin/billing
const TaskBoardV2 = lazy(() => import('./redesign/pages/TaskBoard'));       // /task-board
const WorkflowListV2 = lazy(() => import('./redesign/pages/WorkflowList'));   // /workflows
const WorkflowEditorV2 = lazy(() => import('./redesign/pages/WorkflowEditor'));
const AgentLinkV2 = lazy(() => import('./redesign/pages/AgentLink'));         // /agent/{token} (public guest page)

const standaloneQueryClient = new QueryClient();

// Run once at module load — before React renders — so `?ui=` is consumed and
// `<html data-design>` is set before the first paint.
if (typeof window !== 'undefined') {
  consumeUIQueryParam();
  // `/?apikey=vox_…` sign-in links: move the key out of the URL and into
  // storage before AuthnMeProvider mounts and reads it (apiKeySession.ts).
  consumeApiKeyQueryParam();
  // `/?playground=…` handoff links from the Deepdub voice playground: park the
  // payload in sessionStorage before any auth redirect can drop the query
  // string. The workspace page offers to continue the bot setup from it.
  capturePlaygroundHandoffFromUrl();
  applyDesignAttribute();
}

function RouteLoadingFallback() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <LoadingState variant="fullPage" message="Loading page..." />
    </div>
  );
}

// Auth gate for behind-login operator screens (requireLogin routes only — NOT
// every withAuth route; withAuth merely provides the AuthnMe context, and
// public pages like the /b/{id} dial use it too). When AuthnMe is configured
// and the visitor is not authenticated, bounce to '/' instead of rendering a
// logged-out shell. Waits for isLoading so a refresh doesn't eject an
// authenticated user mid-load. Loop-safe: '/' renders the public home/dial,
// which is never a requireLogin route. `replace` keeps the gated URL out of
// history.
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthnMe();
  const enforced = isAuthnMeConfigured();
  useEffect(() => {
    if (enforced && !isLoading && !isAuthenticated) {
      window.location.replace('/');
    }
  }, [enforced, isLoading, isAuthenticated]);
  if (!enforced) return <>{children}</>;
  if (isLoading || !isAuthenticated) return <RouteLoadingFallback />;
  return <>{children}</>;
}

function renderLazyRoute(page: React.ReactNode, options?: { withTheme?: boolean; withAuth?: boolean; requireLogin?: boolean; fallback?: React.ReactNode }) {
  const {
    withTheme = true,
    withAuth = false,
    requireLogin = false,
    fallback = <RouteLoadingFallback />,
  } = options ?? {};

  const content = (
    <QueryClientProvider client={standaloneQueryClient}>
      <Suspense fallback={fallback}>{page}</Suspense>
    </QueryClientProvider>
  );

  if (!withTheme) {
    return content;
  }

  if (!withAuth && !requireLogin) {
    return <ThemeProvider>{content}</ThemeProvider>;
  }

  // requireLogin implies the AuthnMe context, then gates on being logged in.
  const inner = requireLogin ? <RequireAuth>{content}</RequireAuth> : content;
  return (
    <ThemeProvider>
      <AuthnMeProvider>
        <PreferencesGate>{inner}</PreferencesGate>
      </AuthnMeProvider>
    </ThemeProvider>
  );
}

function App() {
  useSetFavicon();
  // Re-render whenever the URL changes (pushState / popstate). Lets v2
  // internal navigation feel SPA-fluid without remounting App from scratch.
  useUrl();
  const urlParams = getUrlParams();
  // Read the UI flag once per render. The ONLY place this is referenced.
  // Legacy pages don't know v2 exists.
  const v2Active = getUI() === 'v2' && !urlParams.embed;

  console.log('App URL params:', urlParams);

  // Embedded bot dial (?embed=true via iframe / widget.js). When v2 is the
  // active UI, render the v2 dial in embed mode instead of falling back to the
  // legacy FullApp embed — that fallback shows nothing for v2 bots (the dial
  // works directly at /b/{id} but the embed went to v1). getUI() is read here
  // directly because v2Active intentionally excludes embeds.
  if (urlParams.embed && getUI() === 'v2') {
    if (typeof document !== 'undefined') document.body.classList.add('embed-mode');
    // Helper/sidekick mode always uses the sidekick design (not the dial).
    // Its schema arrives via the helper bridge (voxcore-helper-init postMessage).
    if (urlParams.helper) {
      return renderLazyRoute(
        <SidekickHelperV2 position={urlParams.position === 'left' ? 'bottom-left' : 'bottom-right'} />,
        { withAuth: true, fallback: null },
      );
    }
    return renderLazyRoute(<BotDialV2 />, { withAuth: true, fallback: null });
  }

  // v2 path: a single React Router owns all v2 routing, with Theme + AuthnMe
  // providers mounted once above the route outlet so navigation no longer
  // re-inits context or refetches the bot config. Surfaces with no v2 variant
  // (widget/headless/demo/move-bot/accept-tenant) keep rendering exactly as the
  // legacy path does — checked here first — then everything else goes through
  // the router. The legacy if/else below is unchanged and only runs when
  // !v2Active.

  // Full-page bot workflow editor with React Flow + floating prompt composer.
  // Standalone (not behind the v2 flag) — checked before the v2 router so the
  // editor renders identically whether or not the v2 UI flag is set. (Otherwise
  // the v2 RouterProvider below swallows the URL via its catch-all and shows the
  // bot dial, since the v2 router has no conversation/canvas route.)
  if (urlParams.isBotWorkflowEditorPage) {
    return renderLazyRoute(<WorkflowEditorV2 />, { withAuth: true, requireLogin: true });
  }

  // Guest agent page /agent/{token} — PUBLIC (the tokenized link is the whole
  // credential; guests have no account). Standalone like the workflow editor:
  // checked before the v2 router so it renders identically in both UI modes,
  // with no auth provider and no login gate.
  if (urlParams.isAgentLinkPage) {
    return renderLazyRoute(<AgentLinkV2 />);
  }

  if (v2Active) {
    if (urlParams.mode === 'headless') {
      return renderLazyRoute(<HeadlessWidget />, { withTheme: false, fallback: null });
    }
    if (urlParams.mode === 'widget') {
      if (urlParams.theme === 'dark' || urlParams.theme === 'light') {
        document.body.dataset.theme = urlParams.theme;
      }
      return renderLazyRoute(<Widget position={urlParams.position} trigger={urlParams.trigger} />, { withTheme: false });
    }
    if (urlParams.isDemoPage) {
      return renderLazyRoute(<DemoPage variant={urlParams.demoVariant || 1} />, { withTheme: false });
    }
    if (urlParams.isMoveBotPage) {
      return renderLazyRoute(<MoveBotWithLinkPage />, { withAuth: true });
    }
    if (urlParams.isAcceptTenantPage) {
      return renderLazyRoute(<AcceptTenantPage />, { withAuth: true });
    }
    return <RouterProvider router={getV2Router()} />;
  }

  // Behind-login operator surfaces below use { requireLogin: true }: a
  // logged-out visitor is redirected to '/' instead of seeing a hollow shell.
  // Public pages (bot dial /b/{id}, help, api-docs, privacy, terms, home) and
  // the link/invite flows (move-bot, accept-tenant) deliberately omit it.

  // If admin page, render that (wrapped in AuthnMeProvider for JWT auth)
  if (urlParams.isAdminPage) {
    if (v2Active) return renderLazyRoute(<AdminV2 />, { withAuth: true, requireLogin: true });
    return renderLazyRoute(<AdminPage />, { withAuth: true, requireLogin: true });
  }

  // If admin billing page, render that
  if (urlParams.isAdminBillingPage) {
    if (v2Active) return renderLazyRoute(<AdminBillingV2 />, { withAuth: true, requireLogin: true });
    return renderLazyRoute(<AdminBillingPage />, { withAuth: true, requireLogin: true });
  }

  // If reseller page, render that (wrapped in AuthnMeProvider for JWT auth)
  if (urlParams.isResellerPage) {
    if (v2Active) return renderLazyRoute(<ResellerV2 />, { withAuth: true, requireLogin: true });
    return renderLazyRoute(<ResellerPage />, { withAuth: true, requireLogin: true });
  }

  // If packages page, render that
  if (urlParams.isPackagesPage) {
    if (v2Active) return renderLazyRoute(<PackagesV2 />, { withAuth: true, requireLogin: true });
    return renderLazyRoute(<PackagesPage />, { withAuth: true, requireLogin: true });
  }

  // If billing usage page, render that
  if (urlParams.isBillingPage) {
    if (v2Active) return renderLazyRoute(<BillingUsageV2 />, { withAuth: true, requireLogin: true });
    return renderLazyRoute(<BillingUsagePage />, { withAuth: true, requireLogin: true });
  }

  // If usage page, render that
  if (urlParams.isUsagePage) {
    // v2 folds /usage into the /billing page (same data, fuller chrome).
    if (v2Active) return renderLazyRoute(<BillingUsageV2 />, { withAuth: true, requireLogin: true });
    return renderLazyRoute(<UsagePage />, { withAuth: true, requireLogin: true });
  }

  // If customer page, render that (wrapped in AuthnMeProvider for dual auth)
  if (urlParams.isCustomerPage) {
    if (v2Active) return renderLazyRoute(<CustomerV2 />, { withAuth: true, requireLogin: true });
    return renderLazyRoute(<CustomerPage customerId={urlParams.customerIdFromUrl || undefined} />, { withAuth: true, requireLogin: true });
  }

  // If task board page, render that (wrapped in AuthnMeProvider for JWT auth)
  if (urlParams.isTaskBoardPage) {
    if (v2Active) return renderLazyRoute(<TaskBoardV2 />, { withAuth: true, requireLogin: true });
    return renderLazyRoute(<TaskBoardPage />, { withAuth: true, requireLogin: true });
  }

  // Keep the original conversation-graph URL as an alias for the current
  // standalone workflow editor.
  if (urlParams.isConversationGraphPage) {
    return renderLazyRoute(<WorkflowEditorV2 />, { withAuth: true, requireLogin: true });
  }
  // Legacy customer workflow editor routes no longer have an editor page.
  if (urlParams.isCustomerWorkflowEditorPage) {
    return renderLazyRoute(<WorkflowListPage />, { withAuth: true, requireLogin: true });
  }

  // If workflow list page, render that (wrapped in AuthnMeProvider for JWT auth)
  if (urlParams.isWorkflowListPage) {
    if (v2Active) return renderLazyRoute(<WorkflowListV2 />, { withAuth: true, requireLogin: true });
    return renderLazyRoute(<WorkflowListPage />, { withAuth: true, requireLogin: true });
  }

  // If move-bot page, render that (wrapped in AuthnMeProvider for OAuth).
  // NOT requireLogin: emailed deep-link / OAuth flow that manages its own auth.
  if (urlParams.isMoveBotPage) {
    return renderLazyRoute(<MoveBotWithLinkPage />, { withAuth: true });
  }

  // Switch-tenant accept page — recipient lands here from the email link
  // generated by the invite-existing-user flow. NOT requireLogin: the page
  // itself triggers login when needed (a redirect here would break the invite).
  if (urlParams.isAcceptTenantPage) {
    return renderLazyRoute(<AcceptTenantPage />, { withAuth: true });
  }

  // If call logs page, render that (wrapped in AuthnMeProvider for dual auth)
  if (urlParams.isCallLogsPage) {
    if (v2Active) {
      // Forward v1 all-bots deep links (/call-logs/{callId}?bot={botId}) to the
      // canonical per-bot v2 path so the right call log opens. Idempotent: once
      // rewritten the path starts with /b/ and the mapper returns null.
      const v2Url = legacyCallLogV2Url(window.location.pathname, window.location.search);
      if (v2Url) window.history.replaceState(null, '', v2Url);
      return renderLazyRoute(<CallLogsV2 />, { withAuth: true, requireLogin: true });
    }
    return renderLazyRoute(<CallLogsPage />, { withAuth: true, requireLogin: true });
  }

  // If operate page, render that (wrapped in AuthnMeProvider for dual auth)
  if (urlParams.isOperatePage) {
    if (v2Active) return renderLazyRoute(<OperateV2 />, { withAuth: true, requireLogin: true });
    return renderLazyRoute(<OperatePage />, { withAuth: true, requireLogin: true });
  }

  // If config page, render that (wrapped in AuthnMeProvider for dual auth)
  if (urlParams.isConfigPage) {
    if (v2Active) return renderLazyRoute(<ConfigureV2 />, { withAuth: true, requireLogin: true });
    return renderLazyRoute(<ConfigPage />, { withAuth: true, requireLogin: true });
  }

  // If help page, render that
  if (urlParams.isHelpPage) {
    if (v2Active) return renderLazyRoute(<HelpV2 />, { withAuth: true });
    return renderLazyRoute(<HelpPage />, { withAuth: true });
  }

  // If demo page, render standalone (no ThemeProvider wrapping — demo manages its own styles)
  if (urlParams.isDemoPage) {
    return renderLazyRoute(<DemoPage variant={urlParams.demoVariant || 1} />, { withTheme: false });
  }

  // If API docs page, render that
  if (urlParams.isApiDocsPage) {
    if (v2Active) return renderLazyRoute(<ApiDocsV2 />, { withAuth: true });
    return renderLazyRoute(<ApiDocsPage />, { withAuth: true });
  }

  // If privacy page, render that
  if (urlParams.isPrivacyPage) {
    if (v2Active) return renderLazyRoute(<PrivacyV2 />);
    return renderLazyRoute(<PrivacyPage />);
  }

  // If terms page, render that
  if (urlParams.isTermsPage) {
    if (v2Active) return renderLazyRoute(<TermsV2 />);
    return renderLazyRoute(<TermsPage />);
  }

  // If headless mode, render headless widget (invisible, API-only)
  if (urlParams.mode === 'headless') {
    console.log('Rendering Headless mode');
    return renderLazyRoute(<HeadlessWidget />, { withTheme: false, fallback: null });
  }

  // If widget mode, render widget
  if (urlParams.mode === 'widget') {
    console.log('Rendering Widget mode');
    if (urlParams.theme === 'dark' || urlParams.theme === 'light') {
      document.body.dataset.theme = urlParams.theme;
    }
    return renderLazyRoute(<Widget position={urlParams.position} trigger={urlParams.trigger} />, { withTheme: false });
  }

  // Home page (`/`). HomePageWrapper redirects authenticated users to their
  // workspace (`/customer/{id}`) or admin/reseller landing. Visitors who
  // remain on `/` see the bot's public dial — same UX as legacy. The dial
  // page shows a Sign-In affordance for unauthed users.
  if (urlParams.isHomePage && isAuthnMeConfigured()) {
    return (
      <ThemeProvider>
        <AuthnMeProvider>
          <PreferencesGate>
            <HomePageWrapper>
              {v2Active
                ? <Suspense fallback={<RouteLoadingFallback />}><BotDialV2 /></Suspense>
                : <FullApp showLogin={true} />}
            </HomePageWrapper>
          </PreferencesGate>
        </AuthnMeProvider>
      </ThemeProvider>
    );
  }

  // Bot routes. Two v2 surfaces share the `/b/{id}` namespace:
  //   /b/{id}          → BotDial  — simple public dial (no sidenav)
  //   /b/{id}/manage   → BotHero  — owner overview (rich, with sidenav)
  //   /b/{id}/hero     → BotHero  — legacy alias, routes through the same
  //                                 component so old bookmarks keep working
  // Legacy collapses both into FullApp.
  if (v2Active) {
    const isManage = /^\/b\/[^/]+\/(manage|hero)\/?$/.test(window.location.pathname);
    // /b/{id}/manage is the owner overview (gated); /b/{id} is the public dial (open).
    return renderLazyRoute(isManage ? <BotHeroV2 /> : <BotDialV2 />, { withAuth: true, requireLogin: isManage });
  }
  return (
    <ThemeProvider>
      <AuthnMeProvider>
        <PreferencesGate>
          <FullApp showLogin={urlParams.isHomePage} embed={urlParams.embed} embedTheme={urlParams.theme} />
        </PreferencesGate>
      </AuthnMeProvider>
    </ThemeProvider>
  );
}

interface FullAppProps {
  showLogin?: boolean;
  embed?: boolean;
  embedTheme?: 'light' | 'dark' | null;
}

function getBotIdFromPath(): string | null {
  const m = window.location.pathname.match(/^\/b\/([^/]+)/);
  return m ? m[1] : null;
}

function FullApp({ showLogin = false, embed = false, embedTheme }: FullAppProps) {
  const pageBotId = useMemo(getBotIdFromPath, []);
  const [isRecording, setIsRecording] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [currentResponse, setCurrentResponse] = useState('');
  const [status, setStatus] = useState<'idle' | 'dialing' | 'listening' | 'processing' | 'speaking'>('idle');
  const [isCallActive, setIsCallActive] = useState(false);
  const isCallActiveRef = useRef(false); // Synced ref for use in callbacks (avoids stale closure)
  const [logoData, setLogoData] = useState<{ type: 'svg' | 'image'; content: string }>({ type: 'image', content: '' });
  const [isWaitingForFirstResponse, setIsWaitingForFirstResponse] = useState(false);
  const [botFeatures, setBotFeatures] = useState<BotFeatures | null>(null);
  const [callError, setCallError] = useState<string | null>(null);
  const callErrorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const messageIdRef = useRef(0);
  const pendingCutoffRef = useRef<string | undefined>(undefined);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const dialToneRef = useRef<HTMLAudioElement | null>(null);
  const hasReceivedFirstResponse = useRef(false);
  const disconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handleHangupRef = useRef<() => void>(() => {});

  // Get auth token for bypassing IP rate limits when testing own bots
  const { token: authToken } = useAuthnMe();

  const { playAudio, flushAudio, stopAudio, cutPlayback, isPlaying, analyser, setIncomingSampleRate, setAdaptivePrebufferEnabled, setAutoQualityStepdownEnabled, setSampleRateRequester } = useAudioPlayer();

  // Initialize dial tone audio
  useEffect(() => {
    dialToneRef.current = new Audio('/dialtone_once.wav');
    dialToneRef.current.loop = true;
    return () => {
      if (dialToneRef.current) {
        dialToneRef.current.pause();
        dialToneRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (disconnectTimeoutRef.current) {
        clearTimeout(disconnectTimeoutRef.current);
        disconnectTimeoutRef.current = null;
      }
    };
  }, []);

  // Play/stop dial tone based on waiting state
  useEffect(() => {
    if (isWaitingForFirstResponse && dialToneRef.current) {
      dialToneRef.current.currentTime = 0;
      dialToneRef.current.play().catch(e => console.log('Dial tone play failed:', e));
    } else if (dialToneRef.current) {
      dialToneRef.current.pause();
      dialToneRef.current.currentTime = 0;
    }
  }, [isWaitingForFirstResponse]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, currentTranscript, currentResponse]);

  // Handle incoming WebSocket messages via callback
  const handleMessage = useCallback((message: ServerMessage) => {
    switch (message.type) {
      case 'session_config':
        if (typeof (message as any).sample_rate === 'number') {
          setIncomingSampleRate((message as any).sample_rate);
        }
        if (typeof (message as any).adaptive_prebuffer_enabled === 'boolean') {
          setAdaptivePrebufferEnabled((message as any).adaptive_prebuffer_enabled);
        }
        if (typeof (message as any).auto_quality_stepdown_enabled === 'boolean') {
          setAutoQualityStepdownEnabled((message as any).auto_quality_stepdown_enabled);
        }
        break;
      case 'transcript':
        if (message.transcript !== undefined) {
          setCurrentTranscript(message.transcript);
        }
        if (message.isFinal && message.transcript) {
          const userText = message.transcript;
          // Save bot response (if any) AND user message together in correct order
          setCurrentResponse(prevResponse => {
            setMessages(msgs => {
              const newMessages = [...msgs];
              // First add any pending bot response
              if (prevResponse) {
                newMessages.push({
                  id: ++messageIdRef.current,
                  role: 'bot',
                  text: prevResponse
                });
              }
              // Then add user message
              newMessages.push({
                id: ++messageIdRef.current,
                role: 'user',
                text: userText
              });
              return newMessages;
            });
            return '';
          });
          setCurrentTranscript('');
          setStatus('processing');
        }
        break;

      case 'response':
        // First response received - stop waiting for first response
        if (!hasReceivedFirstResponse.current) {
          hasReceivedFirstResponse.current = true;
          setIsWaitingForFirstResponse(false);
        }
        if (message.response !== undefined) {
          setCurrentResponse(prev => prev + message.response);
        }
        // If this is a final response, add to messages
        // For text messages (chat), we always add them
        // For voice responses, they're handled in the audio case
        if (message.isFinal) {
          // heardText comes from loopHeardText in the streaming loop (path 2: old goroutine
          // still running when interrupt fires). cutoff_info handles path 1 (Gemini done
          // before interrupt) via findLastIndex in-place update — no pendingCutoffRef needed.
          const heardText = message.heardText || undefined;
          setCurrentResponse(prev => {
            if (prev) {
              setMessages(msgs => [...msgs, {
                id: ++messageIdRef.current,
                role: 'bot',
                text: prev,
                cutoff: !!heardText,
                heardText,
              }]);
            }
            return '';
          });
          // Only update status if not in a voice call (voice call manages its own status)
          if (!isCallActive) {
            setStatus('idle');
          }
        }
        break;

      case 'handoff':
        setCurrentResponse(prev => {
          setMessages(msgs => {
            const next = [...msgs];
            if (prev) {
              next.push({
                id: ++messageIdRef.current,
                role: 'bot',
                text: prev,
              });
            }
            next.push({
              id: ++messageIdRef.current,
              role: 'system',
              text: formatHandoffNotice(message.handoff_to),
              handoff: message.handoff_to,
            });
            return next;
          });
          return '';
        });
        if (!isCallActive) {
          setStatus('idle');
        }
        break;

      case 'audio':
        // Guard: don't play audio if call is no longer active (e.g. user hung up)
        if (!isCallActiveRef.current) {
          console.log('⚠️ Ignoring audio message - call not active');
          break;
        }
        if (message.audio) {
          // Stop dial tone on first audio — pre-recorded audio (governance
          // disclosure intro) arrives with no `response` text, so clearing
          // only on `response` left the ring looping over it.
          setIsWaitingForFirstResponse(false);
          playAudio(message.audio, message.isFinal || false);
          setStatus('speaking');
        } else if (message.isFinal) {
          flushAudio();
        }
        if (message.isFinal) {
          console.log('🏁 Audio stream finished');
          // Add final response to message history (only fires if response isFinal didn't already)
          setCurrentResponse(prev => {
            if (prev) {
              setMessages(msgs => [...msgs, {
                id: ++messageIdRef.current,
                role: 'bot',
                text: prev,
                cutoff: false,
                heardText: undefined,
              }]);
            }
            return '';
          });
          setTimeout(() => {
            setStatus('idle');
          }, 1000);
        }
        break;

      case 'control':
        // A Smart Turn barge-in (smart_turn_hold.go): drop the buffered audio.
        if (message.command === 'flush') cutPlayback();
        break;
      case 'cutoff_info':
        // Sent by onInterrupt() the moment a barge-in is detected. We do NOT stop
        // audio in the browser here: the backend cancels the TTS at the source and
        // drains its buffer on barge-in, so the client plays out its small remaining
        // buffer and ends naturally. (Calling stopAudio() here abruptly cut the
        // buffer mid-sample and recreated the AudioContext, causing an audible blip.)
        // Update the last bot message in-place — it's already in state when Gemini
        // finished before the interrupt (the common case). pendingCutoffRef is NOT set
        // here to avoid leaking the heardText onto the next non-interrupted turn.
        if (message.heardText !== undefined) {
          setMessages(msgs => {
            let lastIdx = -1;
            for (let i = msgs.length - 1; i >= 0; i--) {
              if (msgs[i].role === 'bot') { lastIdx = i; break; }
            }
            if (lastIdx === -1) return msgs;
            const updated = [...msgs];
            updated[lastIdx] = { ...updated[lastIdx], cutoff: true, heardText: message.heardText };
            return updated;
          });
        }
        break;

      case 'stop_waiting_for_ai':
        // Guaranteed by the backend on every first-response exit (incl. faults
        // that emit no response/audio) so the dial tone can't ring forever.
        setIsWaitingForFirstResponse(false);
        break;

      case 'waiting_for_ai':
        // Backend signals that we're waiting for AI to generate first response
        // (bot goes first but no welcome message)
        console.log('🎵 Waiting for AI to generate first response - playing dialtone');
        setIsWaitingForFirstResponse(true);
        break;

      case 'bot_features':
        if (message.features) {
          setBotFeatures(message.features);
        }
        break;

      case 'error':
        console.error('Error:', message.error);
        if (isCallActiveRef.current) {
          setCallError(message.error || 'Something went wrong');
          if (callErrorTimeoutRef.current) clearTimeout(callErrorTimeoutRef.current);
          callErrorTimeoutRef.current = setTimeout(() => setCallError(null), 8000);
        } else {
          setStatus('idle');
        }
        break;

      case 'fatal_error':
        console.error('Fatal error:', message.error);
        setCallError(message.error || 'Connection lost. Please redial.');
        setTimeout(() => {
          handleHangupRef.current();
        }, 3000);
        break;
    }
  }, [playAudio, flushAudio, stopAudio, cutPlayback, isCallActive, setIncomingSampleRate, setAdaptivePrebufferEnabled, setAutoQualityStepdownEnabled]);

  const handleConnectionFailed = useCallback(() => {
    console.error('Connection failed after retries');
    setStatus('idle');
    setIsCallActive(false);
    isCallActiveRef.current = false;
    setIsRecording(false);
    setIsWaitingForFirstResponse(false);
    if (dialToneRef.current) {
      dialToneRef.current.pause();
      dialToneRef.current.currentTime = 0;
    }
  }, []);

  const { sendMessage, connectionStatus, connect, disconnect } = useWebSocket({
    onMessage: handleMessage,
    onConnectionFailed: handleConnectionFailed,
    authToken: authToken || undefined, // Pass JWT to bypass IP rate limits for bot owners
  });

  // Wire the audio player's underrun → step-down callback to the WebSocket.
  useEffect(() => {
    setSampleRateRequester((rate) => {
      sendMessage({ type: 'set_audio_rate', sample_rate: rate });
    });
    return () => setSampleRateRequester(null);
  }, [sendMessage, setSampleRateRequester]);

  // Screen share tab
  const screenShareConfig: ScreenShareConfig = useMemo(() => ({
    enabled: botFeatures?.screen_share_tab_enabled ?? false,
    captureMode: botFeatures?.screen_share_capture_mode ?? 'on_user_turn',
    intervalSeconds: botFeatures?.screen_share_interval_seconds ?? 5,
  }), [botFeatures]);

  const sendScreenFrame = useCallback((base64Jpeg: string) => {
    sendMessage({ type: 'screen_share_frame', file_data: base64Jpeg });
  }, [sendMessage]);

  const screenShare = useScreenShareTab({
    config: screenShareConfig,
    onFrame: sendScreenFrame,
  });

  const sendScreenFrameRef = useRef(sendScreenFrame);
  useEffect(() => { sendScreenFrameRef.current = sendScreenFrame; }, [sendScreenFrame]);

  const handleDial = () => {
    if (disconnectTimeoutRef.current) {
      clearTimeout(disconnectTimeoutRef.current);
      disconnectTimeoutRef.current = null;
    }
    setStatus('dialing');
    setMessages([]);
    setCurrentTranscript('');
    setCurrentResponse('');
    setCallError(null);
    messageIdRef.current = 0;
    pendingCutoffRef.current = undefined;
    hasReceivedFirstResponse.current = false;
    setBotFeatures(null);
    connect();
  };

  // Handle text message from chat input
  const handleTextMessage = useCallback((text: string) => {
    // Add user message to chat immediately
    setMessages(msgs => [...msgs, {
      id: ++messageIdRef.current,
      role: 'user',
      text: text
    }]);

    // Auto-connect if not connected
    if (connectionStatus !== 'connected') {
      connect();
    }

    // Send message (will be queued if not connected yet)
    sendMessage({ type: 'text_message', text });
    setStatus('processing');
  }, [connectionStatus, connect, sendMessage]);

  // Handle file upload from chat input
  const handleFileUpload = useCallback(async (file: File, fileType: FileType, mimeType: string, base64: string) => {
    // Add user message showing file attachment
    const preview = fileType === 'image' ? URL.createObjectURL(file) : undefined;
    setMessages(msgs => [...msgs, {
      id: ++messageIdRef.current,
      role: 'user',
      text: `📎 ${file.name}`,
      attachment: {
        type: fileType,
        filename: file.name,
        preview
      }
    }]);

    // Auto-connect if not connected
    if (connectionStatus !== 'connected') {
      connect();
    }

    // Send file to backend (will be queued if not connected yet)
    sendMessage({
      type: 'file_upload',
      file_data: base64,
      file_type: fileType,
      mime_type: mimeType,
      filename: file.name
    });
    setStatus('processing');
  }, [connectionStatus, connect, sendMessage]);

  const handleHangup = () => {
    console.log('🔴 Hanging up call');

    // Mark call as inactive immediately (ref updated synchronously for handleMessage guard)
    isCallActiveRef.current = false;

    // Stop screen sharing
    screenShare.stopShare();

    // Stop any playing audio immediately
    stopAudio();

    // Stop dial tone immediately (don't rely on async useEffect)
    if (dialToneRef.current) {
      dialToneRef.current.pause();
      dialToneRef.current.currentTime = 0;
    }

    // Stop recording immediately
    setIsRecording(false);
    setIsCallActive(false);
    setIsWaitingForFirstResponse(false);

    // Tell the server to end the session, then disconnect locally.
    // end_call triggers immediate server-side cleanup (closes WS from server side).
    if (connectionStatus === 'connected') {
      sendMessage({ type: 'end_call' });
    }
    disconnect();

    setStatus('idle');
    setCallError(null);
    // Don't clear transcript/response - user needs to see them
  };
  handleHangupRef.current = handleHangup;

  // Automatically transition from dialing to call active when connected
  // and start recording immediately
  useEffect(() => {
    if (status === 'dialing' && connectionStatus === 'connected') {
      isCallActiveRef.current = true;
      setIsCallActive(true);
      setIsRecording(true);
      setStatus('listening');
      hasReceivedFirstResponse.current = false;
      // Start recording after a short delay to ensure connection is stable
      setTimeout(() => {
        sendMessage({ type: 'start_recording', timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
      }, 100);
    }
  }, [status, connectionStatus, sendMessage]);

  // Handle unexpected disconnection
  useEffect(() => {
    if (connectionStatus === 'disconnected' && isCallActive) {
      console.log('⚠️ WebSocket disconnected unexpectedly');
      isCallActiveRef.current = false;
      screenShare.stopShare();
      stopAudio();
      if (dialToneRef.current) {
        dialToneRef.current.pause();
        dialToneRef.current.currentTime = 0;
      }
      setIsRecording(false);
      setIsCallActive(false);
      setIsWaitingForFirstResponse(false);
      setStatus('idle');
      disconnect();
    }
  }, [connectionStatus, isCallActive, disconnect, stopAudio, screenShare]);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => !prev);
  }, []);

  const handleAudioData = useCallback((audioData: string) => {
    if (!isCallActive || !isRecording) {
      return;
    }
    sendMessage({
      type: 'audio_data',
      audio: audioData
    });
  }, [isCallActive, isRecording, sendMessage]);

  const voiceRecorder = useMemo(() => {
    return getVoiceRecorderSingleton(handleAudioData, () => {
    }, () => {
    });
  }, [handleAudioData]);

  useEffect(() => {
    if (isRecording) {
      voiceRecorder?.startRecording();
    } else {
      voiceRecorder?.stopRecording();
    }
  }, [isRecording, voiceRecorder]);

  useEffect(() => {
    voiceRecorder?.setMuted(isMuted);
  }, [isMuted, voiceRecorder]);

  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    fetch('/assets/logo.svg', { cache: 'no-store' })
      .then(async (res) => {
        const contentType = res.headers.get('Content-Type') || '';
        if (contentType.includes('svg')) {
          const svg = await res.text();
          setLogoData({ type: 'svg', content: svg });
        } else {
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          blobUrlRef.current = url;
          setLogoData({ type: 'image', content: url });
        }
      })
      .catch((err) => {
        console.warn('Failed to load tenant logo:', err);
      });

    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, []);

  const [displayMode, setDisplayMode] = useState<'inline' | 'mini'>('inline');
  const [iframeDims, setIframeDims] = useState({ w: window.innerWidth, h: window.innerHeight });
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!embed) return;
    document.body.style.background = 'transparent';
    document.documentElement.style.background = 'transparent';
    document.documentElement.style.height = '100%';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.height = '100%';
    document.body.style.overflow = 'hidden';
    document.body.style.margin = '0';
    const rootEl = document.getElementById('root');
    if (rootEl) { rootEl.style.height = '100%'; }
    if (embedTheme === 'dark' || embedTheme === 'light') {
      document.body.dataset.theme = embedTheme;
    }

    const updateIframeDims = () => {
      setIframeDims({
        w: document.documentElement.clientWidth || window.innerWidth,
        h: document.documentElement.clientHeight || window.innerHeight,
      });
    };

    const handleMessage = (event: MessageEvent) => {
      const data = event.data;
      if (data && data.type === 'voxcore-display-mode') {
        if (data.display === 'mini' || data.display === 'inline') {
          setDisplayMode(data.display);
          if (data.display === 'inline') {
            setIsExpanded(false);
          }
          requestAnimationFrame(updateIframeDims);
        }
      }
      if (data && data.type === 'voxcore-expand-ack') {
        setIsExpanded(Boolean(data.expanded));
        requestAnimationFrame(updateIframeDims);
      }
    };
    window.addEventListener('message', handleMessage);

    const onResize = () => updateIframeDims();
    window.addEventListener('resize', onResize);

    const resizeObserver = new ResizeObserver(() => updateIframeDims());
    resizeObserver.observe(document.documentElement);
    updateIframeDims();

    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('resize', onResize);
      resizeObserver.disconnect();
    };
  }, [embed, embedTheme]);

  const isMiniView = embed && (displayMode === 'mini' || iframeDims.w <= 100 || isExpanded);
  const isExpandedView = isExpanded || (displayMode === 'mini' && iframeDims.w > 200);

  const debugMiniExpanded = embed && new URLSearchParams(window.location.search).get('debug_mini_expanded') === 'true';
  const [debugCallActive] = useState(debugMiniExpanded);
  const appClass = ['App', embed ? 'embed-mode' : '', (isMiniView || debugMiniExpanded) ? 'embed-mini' : '', (isCallActive || debugCallActive) ? 'call-active' : '', (isExpandedView || debugMiniExpanded) ? 'embed-expanded' : ''].filter(Boolean).join(' ');

  useEffect(() => {
    if (!embed) return;

    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverscroll = html.style.overscrollBehavior;
    const prevBodyOverscroll = body.style.overscrollBehavior;

    if (isExpandedView) {
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      html.style.overscrollBehavior = 'none';
      body.style.overscrollBehavior = 'none';
    }

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      html.style.overscrollBehavior = prevHtmlOverscroll;
      body.style.overscrollBehavior = prevBodyOverscroll;
    };
  }, [embed, isExpandedView]);

  useEffect(() => {
    if (chatContainerRef.current) {
      requestAnimationFrame(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      });
    }
  }, [displayMode, isExpanded]);

  const handleMinimize = () => {
    try {
      window.parent.postMessage({ type: 'voxcore-minimize' }, '*');
    } catch { /* cross-origin */ }
  };

  // Notify parent frame about call state so it can resize the iframe in mini mode
  useEffect(() => {
    if (!embed) return;
    try {
      window.parent.postMessage({
        type: 'voxcore-embed-resize',
        callActive: isCallActive,
        display: displayMode,
      }, '*');
    } catch { /* cross-origin */ }
  }, [embed, isCallActive, displayMode]);

  return (
    <div className={appClass}>
      {(isExpandedView || debugMiniExpanded) && (
        <button
          className="embed-mini-minimize"
          onClick={handleMinimize}
          aria-label="Minimize"
          title="Minimize"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
            <path d="M19 13H5v-2h14v2z"/>
          </svg>
        </button>
      )}
      <div className="container">
        <header className="header">
          <div className="header-actions">
            <ThemeToggle />
            {showLogin && <LoginButton />}
          </div>
          <div className="header-logo">
            {logoData.type === 'svg' ? (
              <div
                className="app-logo"
                aria-label="Tenant logo"
                // SVG comes from tenant configuration; this is intentionally trusted content.
                dangerouslySetInnerHTML={{ __html: logoData.content }}
              />
            ) : logoData.content ? (
              <img
                className="app-logo"
                src={logoData.content}
                alt="Tenant logo"
              />
            ) : null}
          </div>
        </header>

        <main className="main-content">
          <GlobeDialButton
            status={status}
            isCallActive={isCallActive}
            onDial={handleDial}
            onHangup={handleHangup}
            disabled={status === 'dialing'}
            analyser={analyser}
          />

          {callError && (
            <div className="call-error-banner">
              {callError}
            </div>
          )}

          {(isCallActive || debugCallActive) && (
            <div className="status-display">
              <div className={`status-badge ${status}`}>
                {status === 'listening' && 'Listening...'}
                {status === 'processing' && 'Processing...'}
                {status === 'speaking' && 'Speaking...'}
                {status === 'idle' && 'Ready'}
              </div>
            </div>
          )}

          {/* Chat History */}
          {(messages.length > 0 || currentTranscript || currentResponse) && (
            <div className="chat-container" ref={chatContainerRef}>
              {messages.map((msg) => (
                <div key={msg.id} className={`chat-message ${msg.role}${msg.handoff ? ' handoff' : ''}`}>
                  <div className="chat-bubble" dir={detectTextDirection(msg.text)}>
                    {msg.attachment?.preview && (
                      <img src={msg.attachment.preview} alt={msg.attachment.filename} className="chat-message-attachment" />
                    )}
                    {(() => {
                      const split = msg.cutoff ? cutoffSplit(msg.text, msg.heardText) : null;
                      return split
                        ? <>
                            {split.heard}
                            <span className="cutoff-fade">{split.rest}</span>
                            <span className="cutoff-badge" title="User likely heard up to this point">✂</span>
                          </>
                        : <ChatMarkdown text={msg.text} />;
                    })()}
                  </div>
                </div>
              ))}
              {/* Show current streaming transcript */}
              {currentTranscript && (
                <div className="chat-message user streaming">
                  <div className="chat-bubble" dir={detectTextDirection(currentTranscript)}>
                    <ChatMarkdown text={currentTranscript} />
                  </div>
                </div>
              )}
              {/* Show current streaming response */}
              {currentResponse && (
                <div className="chat-message bot streaming">
                  <div className="chat-bubble" dir={detectTextDirection(currentResponse)}>
                    <ChatMarkdown text={currentResponse} />
                    {isPlaying && <span className="speaking-indicator">🔊</span>}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Screen share controls */}
          {isCallActive && screenShareConfig.enabled && (
            <div className="screen-share-controls">
              {!screenShare.isSharing ? (
                <button
                  className="screen-share-btn"
                  onClick={screenShare.startShare}
                  title="Share a browser tab with the bot"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                    <path d="M20 18c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z"/>
                  </svg>
                  Share Tab
                </button>
              ) : (
                <button
                  className="screen-share-btn sharing"
                  onClick={screenShare.stopShare}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                    <path d="M20 18c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z"/>
                  </svg>
                  Stop Sharing
                </button>
              )}
              {screenShare.error && (
                <span className="screen-share-error">{screenShare.error}</span>
              )}
            </div>
          )}

          {/* Chat Input - only visible when call is active */}
          {(isCallActive || debugCallActive) && (
            <ChatInput
              onSendMessage={handleTextMessage}
              onSendFile={handleFileUpload}
              disabled={status === 'dialing'}
              connectionStatus={connectionStatus}
              isMuted={isMuted}
              onToggleMute={toggleMute}
            />
          )}
        </main>

        <footer className="footer">
          <p>Powered by Deepdub</p>
          <div className="footer-links">
            <a href={pageBotId ? `/b/${pageBotId}/api-docs` : '/api-docs'}>Docs</a>
            <span className="footer-separator">|</span>
            <a href={pageBotId ? `/b/${pageBotId}/agents.md` : '/agents.md'} target="_blank" rel="noopener noreferrer">Agents.md</a>
            <span className="footer-separator">|</span>
            <a href="/privacy">Privacy</a>
            <span className="footer-separator">|</span>
            <a href="/terms">Terms</a>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default App;

